import { defineStore, acceptHMRUpdate } from 'pinia'

import { apiClient } from "@/helpers/api.js"
import { useGlobalStore } from '@/stores/GlobalStore'
import { useInventoryStore } from '@/stores/InventoryStore'
import { useUserStore } from '@/stores/UserStore'
import { useCreateBatchStore } from "@/stores/CreateBatchStore"
import { useConfigStore } from '@/stores/ConfigStore'
import { useCardStore } from '@/stores/CardStore'
import { buildPagination } from '@/helpers/pagination'
import { setToLocalStorageByUserId } from '@/helpers/general'
import { fetchMarketPricesInto } from '@/helpers/marketPrices.js'
import { handleApiError } from '@/services/errorHandler'
import {
  BATCH_CARD_COLUMN_OPTIONS,
  VIEW_OPTIONS,
  BATCH_DETAILS_VIEW_OPTIONS,
  DISCLOSURES,
  DEFAULT_SELECTED_VALUES_FOR_DROPDOWNS,
  BATCH_TYPE_LABEL
} from '@/constants'
import { useQRCode } from '@vueuse/integrations/useQRCode'
import { reorderColumnOptions, columnOptionsInGridOrder, saveColumnOrder } from '@/helpers/inventory.js'

export const useBatchStore = defineStore('BatchStore', {
  state: () => {
    return {
      apiBaseUrl: import.meta.env.VITE_API_HOST,
      batchProcessorBaseUrl: import.meta.env.VITE_BATCH_PROCESSOR_API_HOST,
      gridApi: null,

      batches: [],
      pagination: null,

      // Sidebar "Recent Batches". A SEPARATE slot from `batches` on purpose:
      // that one is owned by the All Batches page and is re-sorted, filtered
      // and paginated by it, so sharing it would make the sidebar flicker
      // through whatever the page happens to be showing.
      recentBatches: [],
      recentBatchesLoading: false,

      indexLoading: true,

      batchKind: '',

      selectedBatch: null,
      selectedBatchId: null,
      selectedBatchName: null,

      // Market-price recommendations for the batch cards list (see
      // `fetchMarketPrices`). Mirrors InventoryStore's fields; both drive the
      // shared `@/helpers/marketPrices` loader.
      marketPrices: null,
      currentMarketPriceController: null,
    
      uploads: [],
      countOfUploadedImagesFromMobile: 0,

      notificationStatus: {},
      
      columnOptions: [...BATCH_CARD_COLUMN_OPTIONS],

      viewOptions: [...VIEW_OPTIONS],
      viewOption: { ...VIEW_OPTIONS[0] },
      selectedBatchDetailsViewOption: BATCH_DETAILS_VIEW_OPTIONS[0].value,

      disclosures: { ...DISCLOSURES },

      isAllFilterItem: false,

      openedPanel: false,

      settings: {
        name: '',
        ...DEFAULT_SELECTED_VALUES_FOR_DROPDOWNS
      },

      batchType: 'sports',

      recentlyUploadedImagesIds: [],

      inspectorExpandedErrorBlock: false,

      rowNode: null,

      qrcodeLink: `${import.meta.env.VITE_MAIN_SITE}/mobile-scan`,
      otpPassword: '',
      generateOtpPasswordLoader: false,
      otpBatchId: null,

      openBatchSettings: false,
    }
  },

  getters: {
    selectedViewOption() {
      if (!this.viewOption) return
      const userStore = useUserStore()
      const savedBatchDetailsViewOption = JSON.parse(localStorage.getItem('batchDetailsViewOption'))

      return (savedBatchDetailsViewOption && savedBatchDetailsViewOption[userStore.user?.id]) || this.viewOption
    },
  },

  actions: {
    setBatchStoreFields(fields) {
      for (const [field, value] of Object.entries(fields)) {
        this[field] = value
      }
    },

    updateViewOptions(availableViewOptions) {
      if (availableViewOptions.length) {
        this.viewOptions = VIEW_OPTIONS.reduce((acc, item) => {
          if (availableViewOptions.includes(item.value)) {
            acc.push(item)
          }
          return acc
        }, [])

        this.viewOptions = [VIEW_OPTIONS[0], ...this.viewOptions]
      } else {
        this.viewOptions = []
      }
    },

    updateColumnOption(option) {
      const userStore = useUserStore()
      const globalStore = useGlobalStore()

      this.columnOptions = this.columnOptions.reduce((acc, item) => {
        item.field === option.field ? acc.push({...item, checked: !item.checked}) :  acc.push(item)

        return acc
      }, [])

      // Persist under the active namespace key. For the batch page this is
      // 'display_columns' (unchanged); for batch-in-inventory it is
      // 'display_columns_inventoryBatch' so column toggles never touch the batch.
      const columnsKey = globalStore.saveActiveFilterFor?.displayColumns || 'display_columns'
      setToLocalStorageByUserId(this.columnOptions, columnsKey, userStore.user.id)
    },

    /**
     * Reorder the columns from the Columns dropdown (drag or Alt+arrows).
     * Takes FIELDS, not indexes — hidden rows make indexes unreliable.
     * Same namespaced key as `updateColumnOption`, so a batch and
     * batch-in-inventory keep separate arrangements.
     */
    moveColumnOption(fromField, toField) {
      const userStore = useUserStore()
      const globalStore = useGlobalStore()

      this.columnOptions = reorderColumnOptions(this.columnOptions, fromField, toField)
      saveColumnOrder(this.columnOptions.map(col => col.field), useUserStore().user?.id)

      const columnsKey = globalStore.saveActiveFilterFor?.displayColumns || 'display_columns'
      setToLocalStorageByUserId(this.columnOptions, columnsKey, userStore.user.id)
    },

    /**
     * Mirror a header drag in the grid back into the dropdown order, so the two
     * surfaces never disagree. No-op when nothing moved.
     */
    syncColumnOptionsOrder(orderedFields) {
      const userStore = useUserStore()
      const globalStore = useGlobalStore()

      const next = columnOptionsInGridOrder(this.columnOptions, orderedFields)
      if (next === this.columnOptions) return

      this.columnOptions = next
      saveColumnOrder(this.columnOptions.map(col => col.field), useUserStore().user?.id)

      const columnsKey = globalStore.saveActiveFilterFor?.displayColumns || 'display_columns'
      setToLocalStorageByUserId(this.columnOptions, columnsKey, userStore.user.id)
    },

    updateSelectedBatch({data = this.selectedBatch, config = {}}) {
      const configStore = useConfigStore()
      const configuration = configStore.config || config

      if (data) {
        this.selectedBatch = data
        this.selectedBatchId = data.id
      }

      if (data && configuration && Object.keys(configuration).length) {
        const sportsAndGamesBasedOnType = data.type === 'tcg'
          ? configuration.games.find(item => item.collx_type_id === data.collx_type_id)
          : configuration[data.type].find(item => item.collx_type_id === data.collx_type_id)

        const titleTemplateKey = Object.keys(configuration.titleTemplates).find(key => configuration.titleTemplates[key].id === data.title_template_id)
        const descriptionTemplateKey = Object.keys(configuration.descriptionTemplates).find(key => configuration.descriptionTemplates[key].id === data.description_template_id)
        this.settings = {
          name: data.name,
          titleTemplates: configuration.titleTemplates[titleTemplateKey] ||  { id: null, name: 'Default CDP Template' },
          descriptionTemplates: configuration.descriptionTemplates[descriptionTemplateKey] || { id: null, name: 'Default CDP Template' },
          sportsAndGamesBasedOnType: sportsAndGamesBasedOnType || { collx_type_id: null, name: data.type === 'tcg' ? 'Assorted Games' : 'Assorted Sports' },
          batchTypes: { name: BATCH_TYPE_LABEL[data.type], id: data.type },
        }

        this.batchType = data.type
      }
    },

    updateToDefault() {
      const createBatchStore = useCreateBatchStore()
      const cardStore = useCardStore()

      this.viewOptions = VIEW_OPTIONS
      this.columnOptions = BATCH_CARD_COLUMN_OPTIONS
      this.selectedBatchDetailsViewOption = BATCH_DETAILS_VIEW_OPTIONS[0].value
      this.selectedBatch = null
      this.selectedBatchId = null
      this.settings = {
        name: '',
        ...DEFAULT_SELECTED_VALUES_FOR_DROPDOWNS
      }

      cardStore.selectedCardId = null
      cardStore.pinnedCardData = {}
      cardStore.itemFilters = {}
      cardStore.selectedRanges = null
      cardStore.selectedCards = {}

      createBatchStore.batchType = 'sports'
      createBatchStore.selectedLockSet = null
    },

    /**
     * @param {string} query  SORT key (`createdAt.desc`) — not a search term.
     * @param {string} search Server-side name search. `GET /batches` accepts
     *   `q`, which is what global search already uses; without it a panel can
     *   only filter the pages it happens to have loaded, so a batch on page 2
     *   was simply unfindable.
     */
    async fetchBatches({ page = 1, status = null, query = 'createdAt.desc', search = '' }) {
      const inventoryStore = useInventoryStore()

      if (inventoryStore.currentFetchController) {
        inventoryStore.currentFetchController.abort()
      }

      this.indexLoading = true

      let url = `/batches?page=${page}&sortBy=${query}`
      if (status) {
        url += `&status=${status}`
      }

      const term = String(search || '').trim()
      if (term) {
        url += `&q=${encodeURIComponent(term)}`
      }

      try {
        const response = await apiClient().get(url)

        const data = response.data

        this.batches = data?.items || []

        this.pagination = buildPagination({ data, totalItemsField: 'total_items' })

        return data.items
      } catch (error) {
        handleApiError(error, { component: 'BatchStore', action: 'fetchBatches' })
      } finally {
        this.indexLoading = false
      }
    },

    /**
     * The 3 (by default) most recently created batches for the sidebar.
     *
     * Same `GET /batches` endpoint the All Batches page uses — no new BE work —
     * but the response goes to its own slot and never touches `pagination`.
     */
    // 6, not 3: the sidebar shows 3 and the Inventory strip 4, and both read
    // this one slot. Keeping the wider set here means neither can starve the
    // other depending on which mounted first.
    async fetchRecentBatches({ limit = 6 } = {}) {
      this.recentBatchesLoading = true

      try {
        const { data } = await apiClient().get(`/batches?page=1&sortBy=createdAt.desc`)
        this.recentBatches = (data?.items || []).slice(0, limit)

        return this.recentBatches
      } catch (error) {
        handleApiError(error, { component: 'BatchStore', action: 'fetchRecentBatches' })
        return []
      } finally {
        this.recentBatchesLoading = false
      }
    },

    fetchPrevPage() {
      if (this.pagination?.prevPage) {
        this.fetchBatches(this.pagination?.prevPage)
      }
    },
    fetchNextPage() {
      if (this.pagination?.nextPage) {
        this.fetchBatches(this.pagination?.nextPage)
      }
    },

    async fetchBatch(batchId) {
      const createBatchStore = useCreateBatchStore()
      try {
        const { data } = await apiClient().get(`/batches/${batchId}`)

        createBatchStore.batchInfo = {
          collx_type_id: data.collx_type_id
        }

        this.updateSelectedBatch({ data })
      } catch (error) {
        handleApiError(error, { component: 'BatchStore', action: 'fetchBatch' })
      }
    },

    // Market prices for the batch cards list. Same contract as Inventory
    // (`{ ok, recommendations }` keyed by card id) — only the endpoint differs,
    // so it delegates to the shared loader. Pass the SAME `query` the cards list
    // used (`fetchSelectedCards`) so prices line up with the current page/filters.
    async fetchMarketPrices({ batchId, query } = {}) {
      const id = batchId || this.selectedBatchId
      if (!id) return

      await fetchMarketPricesInto(this, {
        url: `/batches/${id}/market-prices`,
        params: query,
        context: 'BatchStore',
      })
    },

    async updateBatch(body) {
      const configStore = useConfigStore()

      if (!configStore.config?.hasActiveSubscription) return

      try {
        const { data } = await apiClient().put(`/batches/${this.selectedBatchId}`, body)

        this.batches = this.batches.reduce((acc, element) => {
          if (element.id === data.id) {
            acc.push({ ...element, ...body })
          } else {
            acc.push(element)
          }

          return acc
        }, [])

        if (this.selectedBatch) {
          this.updateSelectedBatch({ data: {...data} })
        }
      } catch (error) {
        handleApiError(error, { component: 'BatchStore', action: 'updateBatch' })
      }
    },

    toggleBatchStatus(batch) {
      this.selectedBatchId = batch.id
      this.updateBatch({ status: batch.status === 'open' ? 'closed' : 'open' })
    },

    async reprocessBatch() {
      const configStore = useConfigStore()
      const cardStore = useCardStore()

      if (!configStore.config?.hasActiveSubscription) return

      const userStore = useUserStore()
      const globalStore = useGlobalStore()

      try {
        globalStore.cellsLoading = true

        if (this.selectedBatchDetailsViewOption !== 'Overview') {
          cardStore.cardsLoading = true
        }

        const data = await apiClient(true).post(`/users/${userStore.user.id}/batches/${this.selectedBatchId}/reprocess`)
      } catch (error) {
        handleApiError(error, { component: 'BatchStore', action: 'reprocessBatch' })
      }
    },

    async addBannerToBatch() {
      const configStore = useConfigStore()

      if (!configStore.config?.hasActiveSubscription) return

      const userStore = useUserStore()

      try {
        await apiClient(true).post(`/users/${userStore.user.id}/batches/${this.selectedBatchId}/add-banner?banner=${userStore.user.banner_image}&location=${userStore.user?.banner_placement || 'top'}`)
      } catch (error) {
        handleApiError(error, { component: 'BatchStore', action: 'addBannerToBatch' })
      }
    },

    async removeBannerFromBatch() {
      const configStore = useConfigStore()

      if (!configStore.config?.hasActiveSubscription) return

      const userStore = useUserStore()

      try {
        await apiClient(true).post(`/users/${userStore.user.id}/batches/${this.selectedBatchId}/remove-banner?banner=${userStore.user.banner_image}`)
      } catch (error) {
        handleApiError(error, { component: 'BatchStore', action: 'removeBannerFromBatch' })
      }
    },

    async removeBatch() {
      try {
        await apiClient().delete(`/batches/${this.selectedBatchId}`)
        this.batches = this.batches.reduce((acc, item) => {
          if (item.id !== this.selectedBatchId) {
            acc.push(item)
          }

          return acc
        }, [])
      } catch (error) {
        handleApiError(error, { component: 'BatchStore', action: 'removeBatch' })
      }
    },

    async getOtpPassword(batchId) {
      try {
        this.generateOtpPasswordLoader = true
        this.otpBatchId = batchId
        const { data } = await apiClient().post(`otp-links`, {
          redirect_to: `/mobile-scan/${batchId}`
        })

        if (data?.ok) {
          const splitLoginUrl = data.otp_login_url.split('/')
          const url = `${import.meta.env.VITE_MAIN_SITE}/otp-links/${splitLoginUrl[splitLoginUrl.length - 1]}`

          this.qrcodeLink = useQRCode(url)
        }

        this.generateOtpPasswordLoader = false
      } catch (error) {
        this.generateOtpPasswordLoader = false
        handleApiError(error, { component: 'BatchStore', action: 'getOtpPassword' })
      }
    },
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useBatchStore, import.meta.hot))
}
